import React, { useState, useEffect } from "react";

import {
  ShieldCheckIcon,
  BellIcon,
  KeyIcon,
  CalendarDaysIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  SwatchIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import toast from "react-hot-toast";
import apiClient from "../api/client";
import { useAuth } from "../context/AuthContext";
import {
  showConfirm,
  showError,
  showSuccess,
  showWarning,
} from "../utils/alert";

const ALL_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const RESULT_BORDER_STYLES = [
  {
    id: "diamond",
    name: "Diamond Pattern",
    description: "Navy, gold and cream patterned frame.",
  },
  {
    id: "classic",
    name: "Classic Double Frame",
    description: "Formal double-line school report border.",
  },
  {
    id: "royal",
    name: "Royal Gold",
    description: "Strong navy and gold ceremonial frame.",
  },
  {
    id: "modern",
    name: "Modern Lines",
    description: "Clean layered lines with a modern finish.",
  },
  {
    id: "traditional",
    name: "Traditional Pattern",
    description: "Bold repeating pattern inspired by printed cards.",
  },
  {
    id: "minimal",
    name: "Minimal Frame",
    description: "Simple, light and professional border.",
  },
  {
    id: "emerald",
    name: "Emerald Crest",
    description: "Deep green and gold frame with a premium school feel.",
  },
  {
    id: "heritage",
    name: "Heritage Corners",
    description: "Elegant corner ornaments with a formal inner frame.",
  },
  {
    id: "executive",
    name: "Executive Navy",
    description: "Strong navy lines with clean gold accents.",
  },
  {
    id: "sunburst",
    name: "Golden Sunburst",
    description: "Warm gold patterned border with refined detail.",
  },
  {
    id: "woven",
    name: "Woven School Pattern",
    description: "Interlaced navy and gold pattern around the card.",
  },
  {
    id: "prestige",
    name: "Prestige Layered Frame",
    description: "Multi-layered formal border for premium report cards.",
  },
  {
    id: "word_art_deco",
    name: "Word Art Deco",
    description: "Geometric corner blocks inspired by Microsoft Word page art.",
  },
  {
    id: "word_victorian",
    name: "Word Victorian",
    description: "Ornamental formal frame with a traditional document style.",
  },
  {
    id: "word_flourish",
    name: "Word Flourish",
    description: "Curved decorative flourishes for a refined report cover.",
  },
  {
    id: "word_certificate",
    name: "Word Certificate",
    description: "A formal certificate-style border with layered gold lines.",
  },
  {
    id: "word_celtic",
    name: "Word Celtic Knot",
    description: "Interlocking geometric pattern similar to Word art borders.",
  },
  {
    id: "word_corner_leaf",
    name: "Word Leaf Corners",
    description: "Clean botanical corner ornaments with a simple inner line.",
  },
  {
    id: "word_blue_scroll",
    name: "Word Blue Scroll",
    description: "Blue ornamental scroll pattern for a classic school document.",
  },
  {
    id: "word_gold_chain",
    name: "Word Gold Chain",
    description: "Linked gold pattern with a formal navy inner frame.",
  },
];

const getBorderPreviewClass = (style) => {
  const classes = {
    diamond:
      "border-[6px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-linear-gradient(45deg,#1f3557_0_10px,#d9a629_10px_17px,#f7e8a7_17px_24px,#1f3557_24px_34px)_border-box]",
    classic:
      "border-[5px] border-double border-[#1f3557]",
    royal:
      "border-[6px] border-[#b88a18] ring-2 ring-inset ring-[#1f3557]",
    modern:
      "border-[4px] border-[#1f3557] ring-[5px] ring-inset ring-[#d9a629]/70",
    traditional:
      "border-[6px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-linear-gradient(90deg,#1f3557_0_14px,#d9a629_14px_21px,#1f3557_21px_30px)_border-box]",
    minimal:
      "border-2 border-[#1f3557] ring-1 ring-inset ring-[#d9a629]",
    emerald:
      "border-[6px] border-[#14532d] ring-[3px] ring-inset ring-[#d4af37]",
    heritage:
      "border-[4px] border-[#6b4f2a] ring-[3px] ring-inset ring-[#c9a46c]",
    executive:
      "border-[7px] border-[#172554] ring-[2px] ring-inset ring-[#d4af37]",
    sunburst:
      "border-[7px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-conic-gradient(from_45deg,#d4af37_0deg_12deg,#f7e8a7_12deg_24deg,#8a6a17_24deg_36deg)_border-box]",
    woven:
      "border-[7px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-linear-gradient(45deg,#1f3557_0_8px,#d9a629_8px_12px,#1f3557_12px_20px)_border-box]",
    prestige:
      "border-[4px] border-[#1f3557] ring-[6px] ring-inset ring-[#d9a629]/60 outline outline-2 outline-offset-[-12px] outline-[#1f3557]/40",
    word_art_deco:
      "border-[7px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-linear-gradient(45deg,#172554_0_9px,#d4af37_9px_13px,#f8fafc_13px_17px,#172554_17px_26px)_border-box]",
    word_victorian:
      "border-[6px] border-double border-[#6b4f2a] ring-[3px] ring-inset ring-[#d7b77a]",
    word_flourish:
      "border-[6px] border-[#1e3a5f] ring-[4px] ring-inset ring-[#c7a34b]/80",
    word_certificate:
      "border-[4px] border-[#8a6a17] ring-[7px] ring-inset ring-[#f3df9b]/80 outline outline-2 outline-offset-[-13px] outline-[#172554]",
    word_celtic:
      "border-[7px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-linear-gradient(135deg,#14532d_0_7px,#d4af37_7px_11px,#14532d_11px_18px,#f8fafc_18px_21px)_border-box]",
    word_corner_leaf:
      "border-[3px] border-[#166534] ring-[2px] ring-inset ring-[#d4af37]",
    word_blue_scroll:
      "border-[7px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-linear-gradient(90deg,#1e3a8a_0_12px,#93c5fd_12px_16px,#f8fafc_16px_20px,#1e3a8a_20px_30px)_border-box]",
    word_gold_chain:
      "border-[7px] border-transparent [background:linear-gradient(white,white)_padding-box,repeating-linear-gradient(45deg,#b88a18_0_6px,#f5e7a6_6px_10px,#7c5d12_10px_16px,#172554_16px_20px)_border-box]",
  };

  return classes[style] || classes.diamond;
};

const unwrapList = (response, keys = []) => {
  const value = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(value)) return value;
  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return value?.rows || [];
};

export default function Settings() {
  const { user } = useAuth();
  const userRole = String(user?.role || "").toLowerCase();
  const canManageTimetable = [
    "admin",
    "school_admin",
    "super_admin",
  ].includes(userRole);
  const canManageResultTemplate = canManageTimetable;

  const [activeTab, setActiveTab] = useState(
    canManageTimetable ? "teaching-days" : "security",
  );
  const [loading, setLoading] = useState(true);
  const [savingTimetable, setSavingTimetable] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [loadingResultTemplate, setLoadingResultTemplate] = useState(false);
  const [savingResultTemplate, setSavingResultTemplate] = useState(false);
  const [selectedBorderStyle, setSelectedBorderStyle] = useState("diamond");
  const [teachingDays, setTeachingDays] = useState([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
  ]);
  const [periods, setPeriods] = useState([]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Timetable setup now lives here instead of on the Master Timetable page.
  useEffect(() => {
    if (!canManageTimetable) {
      setLoading(false);
      return undefined;
    }

    const fetchTimetableSetup = async () => {
      try {
        setLoading(true);
        const [settingsResponse, periodsResponse] = await Promise.all([
          apiClient.get("/timetables/settings"),
          apiClient.get("/timetables/periods"),
        ]);
        const configuredDays = settingsResponse?.data?.data?.teaching_days;
        if (Array.isArray(configuredDays) && configuredDays.length) {
          setTeachingDays(configuredDays);
        }
        setPeriods(unwrapList(periodsResponse, ["periods"]));
      } catch (error) {
        console.error("Failed to fetch timetable setup:", error);
        await showError({
          title: "Unable to Load Setup",
          text: "The timetable setup could not be loaded.",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchTimetableSetup();
    return undefined;
  }, [canManageTimetable]);

  // Load the school result-template choice independently of timetable setup.
  useEffect(() => {
    if (!canManageResultTemplate) return undefined;

    let active = true;

    const fetchResultTemplateSettings = async () => {
      try {
        setLoadingResultTemplate(true);

        const response = await apiClient.get("/schools/profile");
        const school = response?.data?.data || {};

        if (active) {
          setSelectedBorderStyle(
            school.resultBorderStyle ||
              school.result_border_style ||
              "diamond",
          );
        }
      } catch (error) {
        console.error(
          "Failed to load result template settings:",
          error,
        );

        if (active) {
          await showError({
            title: "Unable to Load Result Template",
            text:
              error?.response?.data?.message ||
              "The saved result border could not be loaded.",
          });
        }
      } finally {
        if (active) setLoadingResultTemplate(false);
      }
    };

    fetchResultTemplateSettings();

    return () => {
      active = false;
    };
  }, [canManageResultTemplate]);

  // If the authenticated role changes, never leave a non-admin user on an
  // administrator-only tab.
  useEffect(() => {
    if (
      !canManageTimetable &&
      ["teaching-days", "periods", "result-template"].includes(activeTab)
    ) {
      setActiveTab("security");
    }
  }, [activeTab, canManageTimetable]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDay = (day) => {
    if (!canManageTimetable) return;
    setTeachingDays((current) =>
      current.includes(day)
        ? current.filter((item) => item !== day)
        : ALL_DAYS.filter((item) => [...current, day].includes(item)),
    );
  };

  const saveTeachingDays = async () => {
    if (!canManageTimetable) {
      await showWarning({
        title: "Administrator Access Required",
        text: "Only a school administrator can change teaching days.",
      });
      return;
    }
    if (!teachingDays.length) {
      await showWarning({
        title: "Teaching Day Required",
        text: "Select at least one teaching day before saving.",
      });
      return;
    }
    setSavingTimetable(true);
    try {
      await apiClient.put("/timetables/settings", {
        teaching_days: teachingDays,
      });
      await showSuccess({
        title: "Teaching Days Saved",
        text: "The school timetable days were updated successfully.",
      });
    } catch (error) {
      await showError({
        title: "Unable to Save Teaching Days",
        text: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSavingTimetable(false);
    }
  };

  const updatePeriod = (index, field, value) => {
    if (!canManageTimetable) return;
    setPeriods((current) =>
      current.map((period, position) =>
        position === index ? { ...period, [field]: value } : period,
      ),
    );
  };

  const addPeriod = () => {
    if (!canManageTimetable) return;
    setPeriods((current) => [
      ...current,
      {
        name: `Period ${current.length + 1}`,
        period_number: current.length + 1,
        start_time: "08:00",
        end_time: "08:40",
        period_type: "lesson",
        is_teaching_period: true,
        is_active: true,
      },
    ]);
  };

  const removePeriod = async (index) => {
    if (!canManageTimetable) {
      await showWarning({
        title: "Administrator Access Required",
        text: "Only a school administrator can remove timetable periods.",
      });
      return;
    }
    const period = periods[index];
    const confirmed = await showConfirm({
      title: "Remove Period",
      text: `Remove “${period?.name || `Period ${index + 1}`}” from the period configuration?`,
      confirmText: "Yes, remove period",
      icon: "warning",
    });
    if (confirmed)
      setPeriods((current) =>
        current.filter((_, position) => position !== index),
      );
  };

  const savePeriods = async () => {
    if (!canManageTimetable) {
      await showWarning({
        title: "Administrator Access Required",
        text: "Only a school administrator can change timetable periods.",
      });
      return;
    }
    if (!periods.length) {
      await showWarning({
        title: "Period Required",
        text: "Add at least one timetable period before saving.",
      });
      return;
    }
    const invalidPeriod = periods.find(
      (period) =>
        !String(period.name || "").trim() ||
        !period.start_time ||
        !period.end_time ||
        String(period.start_time).slice(0, 5) >=
          String(period.end_time).slice(0, 5),
    );
    if (invalidPeriod) {
      await showWarning({
        title: "Check Period Details",
        text: "Every period needs a name and an end time later than its start time.",
      });
      return;
    }
    setSavingTimetable(true);
    try {
      await apiClient.put("/timetables/periods", {
        periods: periods.map((period, index) => ({
          name: period.name,
          period_number: Number(period.period_number || index + 1),
          start_time: period.start_time,
          end_time: period.end_time,
          period_type: period.period_type,
          is_teaching_period: period.period_type === "lesson",
          is_active: true,
        })),
      });
      const response = await apiClient.get("/timetables/periods");
      setPeriods(unwrapList(response, ["periods"]));
      await showSuccess({
        title: "Periods Saved",
        text: "The school timetable periods were updated successfully.",
      });
    } catch (error) {
      await showError({
        title: "Unable to Save Periods",
        text: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSavingTimetable(false);
    }
  };

  const saveResultBorderStyle = async () => {
    if (!canManageResultTemplate) {
      await showWarning({
        title: "Administrator Access Required",
        text:
          "Only a school administrator can change the result template.",
      });
      return;
    }

    setSavingResultTemplate(true);

    try {
      const response = await apiClient.put("/schools/profile", {
        resultBorderStyle: selectedBorderStyle,
      });

      const savedSchool = response?.data?.data || {};
      setSelectedBorderStyle(
        savedSchool.resultBorderStyle ||
          savedSchool.result_border_style ||
          selectedBorderStyle,
      );

      await showSuccess({
        title: "Result Border Saved",
        text:
          "This border design will now be used on the school's front result templates.",
      });
    } catch (error) {
      await showError({
        title: "Unable to Save Result Border",
        text:
          error?.response?.data?.message ||
          "The selected result border could not be saved.",
      });
    } finally {
      setSavingResultTemplate(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setSavingPassword(true);
    try {
      const res = await apiClient.post("/auth/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (res.data?.success) {
        toast.success("Password changed successfully!");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        toast.error(res.data?.message || "Failed to change password");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const tabs = [
    ...(canManageTimetable
      ? [
          { id: "teaching-days", name: "Teaching Days", icon: CalendarDaysIcon },
          { id: "periods", name: "Periods", icon: ClockIcon },
          { id: "result-template", name: "Result Template", icon: SwatchIcon },
        ]
      : []),
    { id: "security", name: "Security", icon: ShieldCheckIcon },
    { id: "notification", name: "Notifications", icon: BellIcon },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      {/* Single flat word-processor style menu bar */}
      <nav className="mb-6 border-y border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <div className="flex min-w-max items-stretch px-1">
            {tabs.map((tab) => {
              const selected = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative inline-flex min-w-max items-center gap-2 border-r border-gray-200 px-5 py-3.5 text-sm font-medium transition-colors first:border-l ${
                    selected
                      ? "bg-primary-50 text-primary-700"
                      : "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  {React.createElement(tab.icon, {
                    className: `h-4 w-4 ${
                      selected ? "text-primary-600" : "text-gray-400"
                    }`,
                  })}
                  <span>{tab.name}</span>

                  {selected && (
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {canManageTimetable &&
        ["teaching-days", "periods"].includes(activeTab) && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-sm text-gray-500">
              Loading timetable setup...
            </div>
          ) : activeTab === "teaching-days" ? (
            <section className="p-5 sm:p-7">
              <div className="flex items-start gap-3">
                <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600">
                  <CalendarDaysIcon className="h-6 w-6" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    School Teaching Days
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Select the days on which lessons are taught. The timetable
                    will use them in this order.
                  </p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                {ALL_DAYS.map((day) => {
                  const selected = teachingDays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={`rounded-xl border px-3 py-4 text-sm font-semibold transition ${selected ? "border-primary-500 bg-primary-50 text-primary-700 ring-2 ring-primary-100" : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"}`}
                    >
                      <span
                        className={`mx-auto mb-2 flex h-5 w-5 items-center justify-center rounded-full border text-xs ${selected ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300"}`}
                      >
                        {selected ? "✓" : ""}
                      </span>
                      {day}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={saveTeachingDays}
                  disabled={savingTimetable || !teachingDays.length}
                  className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-50"
                >
                  {savingTimetable ? "Saving..." : "Save Teaching Days"}
                </button>
              </div>
            </section>
          ) : (
            <section className="p-5 sm:p-7">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600">
                    <ClockIcon className="h-6 w-6" />
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      School Periods
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Set lesson times and add registration, devotion, breaks,
                      lunch or activities.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addPeriod}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100"
                >
                  <PlusIcon className="h-4 w-4" /> Add Period
                </button>
              </div>
              <div className="mt-6 space-y-3">
                {periods.map((period, index) => (
                  <div
                    key={period.id || index}
                    className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:grid-cols-2 lg:grid-cols-[1.4fr_.65fr_1fr_1fr_1fr_auto]"
                  >
                    <label className="text-xs font-medium text-gray-500">
                      Period Name
                      <input
                        value={period.name || ""}
                        onChange={(event) =>
                          updatePeriod(index, "name", event.target.value)
                        }
                        className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-500">
                      Number
                      <input
                        type="number"
                        min="1"
                        value={period.period_number || index + 1}
                        onChange={(event) =>
                          updatePeriod(
                            index,
                            "period_number",
                            event.target.value,
                          )
                        }
                        className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-500">
                      Starts
                      <input
                        type="time"
                        value={String(period.start_time || "").slice(0, 5)}
                        onChange={(event) =>
                          updatePeriod(index, "start_time", event.target.value)
                        }
                        className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-500">
                      Ends
                      <input
                        type="time"
                        value={String(period.end_time || "").slice(0, 5)}
                        onChange={(event) =>
                          updatePeriod(index, "end_time", event.target.value)
                        }
                        className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-500">
                      Type
                      <select
                        value={period.period_type || "lesson"}
                        onChange={(event) =>
                          updatePeriod(index, "period_type", event.target.value)
                        }
                        className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      >
                        <option value="lesson">Lesson</option>
                        <option value="registration">Registration</option>
                        <option value="devotion">Devotion</option>
                        <option value="break">Break</option>
                        <option value="lunch">Lunch</option>
                        <option value="assembly">Assembly</option>
                        <option value="activity">Activity</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => removePeriod(index)}
                      className="self-end rounded-lg border border-red-100 bg-white p-2.5 text-red-600 transition hover:bg-red-50"
                      title="Remove period"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                {!periods.length && (
                  <div className="rounded-xl border-2 border-dashed border-gray-200 p-10 text-center text-sm text-gray-500">
                    No periods configured. Select “Add Period” to begin.
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={savePeriods}
                  disabled={savingTimetable || !periods.length}
                  className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-50"
                >
                  {savingTimetable ? "Saving..." : "Save Periods"}
                </button>
              </div>
            </section>
          )}
        </div>
      )}

      {canManageResultTemplate &&
        activeTab === "result-template" && (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 bg-gray-50/80 px-5 py-5 sm:px-7">
              <div className="flex items-start gap-3">
                <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600">
                  <SwatchIcon className="h-6 w-6" />
                </span>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Front Result Border Design
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Select the border art that will appear on every
                    front result template generated by this school.
                  </p>
                </div>
              </div>
            </div>

            {loadingResultTemplate ? (
              <div className="p-12 text-center text-sm text-gray-500">
                Loading result template settings...
              </div>
            ) : (
              <section className="p-5 sm:p-7">
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {RESULT_BORDER_STYLES.map((style) => {
                    const selected =
                      selectedBorderStyle === style.id;

                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() =>
                          setSelectedBorderStyle(style.id)
                        }
                        className={`group rounded-2xl border p-3 text-left transition ${
                          selected
                            ? "border-primary-500 bg-primary-50 ring-2 ring-primary-100"
                            : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`relative aspect-[1.42/1] overflow-hidden rounded-xl bg-white p-3 ${getBorderPreviewClass(
                            style.id,
                          )}`}
                        >
                          <div className="flex h-full flex-col items-center justify-center text-center">
                            <div className="h-8 w-8 rounded-full border-2 border-gray-300 bg-gray-50" />
                            <div className="mt-2 h-2 w-24 rounded bg-gray-300" />
                            <div className="mt-1 h-1.5 w-16 rounded bg-gray-200" />
                            <div className="mt-4 h-1.5 w-28 rounded bg-gray-200" />
                            <div className="mt-1.5 h-1.5 w-24 rounded bg-gray-200" />
                          </div>

                          {selected && (
                            <span className="absolute right-2 top-2 rounded-full bg-primary-600 p-1 text-white shadow-sm">
                              <CheckCircleIcon className="h-5 w-5" />
                            </span>
                          )}
                        </div>

                        <div className="px-1 pb-1 pt-3">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-gray-900">
                              {style.name}
                            </h3>

                            {selected && (
                              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary-700">
                                Selected
                              </span>
                            )}
                          </div>

                          <p className="mt-1 text-xs leading-5 text-gray-500">
                            {style.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-7 flex flex-col gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-500">
                    The saved design is applied automatically when
                    the front result card is downloaded.
                  </p>

                  <button
                    type="button"
                    onClick={saveResultBorderStyle}
                    disabled={savingResultTemplate}
                    className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingResultTemplate
                      ? "Saving Border..."
                      : "Save Border Design"}
                  </button>
                </div>
              </section>
            )}
          </div>
        )}

      {/* Security Tab - Change Password */}
      {activeTab === "security" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 max-w-xl">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold">Change Password</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                placeholder="Enter new password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          <div className="mt-8">
            <button
              onClick={handleChangePassword}
              disabled={savingPassword}
              className="w-full md:w-auto bg-primary-600 text-white px-10 py-3 rounded-xl hover:bg-primary-700 transition disabled:opacity-70 font-medium flex items-center justify-center gap-2"
            >
              <KeyIcon className="w-5 h-5" />
              {savingPassword ? "Changing Password..." : "Change Password"}
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Password must be at least 6 characters long.
          </p>
        </div>
      )}

      {/* Notifications Tab (Placeholder) */}
      {activeTab === "notification" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <BellIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700">
            Notifications Settings
          </h3>
          <p className="text-gray-500 mt-2">Coming soon...</p>
        </div>
      )}
    </div>
  );
}
